import { Warning } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";

import useFileProxy from "../../../datahooks/useFileProxy";
import { extractWaveform, isBlobUrl } from "../../../utils";
import AudioPlayerLoader from "./AudioPlayerLoader";
import PlaybackControls from "./PlaybackControls";
import TimerDisplay from "./TimerDisplay";
import WaveformCanvas from "./WaveformCanvas";

interface AudioPlayerProps {
  audioUrl: string;
  accountId: string;
  duration: number;
  onError?: (error: string) => void;
}

export default function AudioPlayer({
  audioUrl,
  accountId,
  duration,
  onError = null,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [waveform, setWaveform] = useState<number[] | null>(null);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const { fileProxyData, isLoadingFileProxy, fileProxyError } = useFileProxy({
    accountId,
    url: audioUrl,
    disableQuery: isBlobUrl(audioUrl),
  });

  const fileUrl = isBlobUrl(audioUrl) ? audioUrl : objectUrl;

  useEffect(() => {
    if (fileProxyData) {
      // Detect Safari
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      );

      let blobToUse = fileProxyData;

      // Workaround: force type to audio/mpeg for Safari
      if (isSafari && fileProxyData.type === "application/octet-stream") {
        blobToUse = new Blob([fileProxyData], { type: "audio/mpeg" });
      }

      const proxyUrl = URL.createObjectURL(blobToUse);
      setObjectUrl(proxyUrl);

      return () => URL.revokeObjectURL(proxyUrl);
    }
    setObjectUrl(null);
  }, [fileProxyData]);

  // Handle audio end
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
      setPlaybackTime(0);
    };
    const handleLoadedMetadata = () => {
      setIsLoading(false);
    };
    const handleError = () => {
      setHasError(true);
      setIsLoading(false);
      onError?.("Failed to load audio file");
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("error", handleError);
    };
  }, [fileUrl, onError]);

  // Generate waveform when audio loads
  useEffect(() => {
    if (!fileUrl || isLoading || isLoadingFileProxy) return;

    const generateWaveform = async () => {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to fetch audio");

      const blob = await response.blob();

      const waveformData = await extractWaveform(
        blob,
        43, // 216px / 5 = 43 bars
        27, // canvas height
      );
      setWaveform(waveformData);
    };

    generateWaveform();
  }, [fileUrl, isLoading, isLoadingFileProxy]);

  // Function to pause all other audio elements
  const pauseAllOtherAudios = useCallback((currentAudio: HTMLAudioElement) => {
    const allAudios = document.querySelectorAll("audio");
    for (let i = 0; i < allAudios.length; i += 1) {
      const audioElement = allAudios[i];
      if (audioElement !== currentAudio && !audioElement.paused) {
        audioElement.pause();
        // Reset to beginning
        audioElement.currentTime = 0;
      }
    }
  }, []);

  const handlePlay = useCallback(() => {
    if (audioRef.current) {
      pauseAllOtherAudios(audioRef.current);
      setIsPlaying(true);
      audioRef.current.play();
    }
  }, [pauseAllOtherAudios]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    audioRef.current?.pause();
  }, []);

  if (hasError || fileProxyError) {
    return (
      <div className="flex items-center gap-1">
        <Warning className="text-red-600" />
        <span className="text-sm text-red-600">Failed to load audio</span>
      </div>
    );
  }

  if (isLoadingFileProxy) {
    return <AudioPlayerLoader />;
  }

  return (
    <div className="flex w-full flex-row items-center justify-between gap-x-2 rounded-full lg:mt-1 lg:w-fit lg:justify-center">
      <PlaybackControls
        isPlaying={isPlaying}
        onPlay={handlePlay}
        onPause={handlePause}
      />

      {/* Waveform canvas */}
      <WaveformCanvas
        isRecording={false}
        hasAudio
        waveform={waveform}
        isPlaying={isPlaying}
        playbackTime={playbackTime}
        duration={duration / 1000}
        currentTime={
          isPlaying && audioRef.current
            ? audioRef.current.currentTime
            : playbackTime
        }
      />

      <TimerDisplay timer={playbackTime || duration / 1000} />

      {/* Audio element for playback */}
      <audio
        ref={audioRef}
        src={fileUrl}
        className="sr-only"
        onPlay={() => setIsPlaying(true)}
        onPause={(e) => {
          setIsPlaying(false);
          setPlaybackTime(e.currentTarget.currentTime);
        }}
        onTimeUpdate={(e) => setPlaybackTime(e.currentTarget.currentTime)}
      >
        <track kind="captions" />
      </audio>
    </div>
  );
}
